import { useCallback, useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button, input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  // `:disabled` also covers controls inside a disabled fieldset
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.matches(':disabled'));
}

function focusEdge(root: HTMLElement | null, edge: 'first' | 'last') {
  if (!root) return;
  const items = focusables(root);
  const target = edge === 'first' ? items[0] : items[items.length - 1];
  (target ?? root).focus();
}

/**
 * Moves focus into an open dialog, keeps it there and hands it back to whatever had it
 * when the dialog closes: the page behind must stay out of reach. Tab wraps on keydown;
 * focus that gets out anyway (Tab out of an embedded iframe never reaches this document)
 * is caught by the returned edge guards or pulled back on focusin.
 */
export function useDialogFocus(open: boolean, panel: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      const root = panel.current;
      if (e.key !== 'Tab' || !root) return;
      const items = focusables(root);
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const inside = active instanceof Node && root.contains(active) && active !== root;
      if (!first || !last) {
        e.preventDefault();
        root.focus();
      } else if (e.shiftKey && (!inside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (e: FocusEvent) => {
      const root = panel.current;
      const target = e.target;
      if (!root || !(target instanceof HTMLElement)) return;
      if (root.contains(target) || target.dataset['focusGuard']) return;
      focusEdge(root, 'first');
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      opener?.focus();
    };
  }, [open, panel]);

  const toFirst = useCallback(() => focusEdge(panel.current, 'first'), [panel]);
  const toLast = useCallback(() => focusEdge(panel.current, 'last'), [panel]);
  return { toFirst, toLast };
}
